/**
 * AdminUserActivityDialog — 1/7/30-day analytics modal for a single
 * user, opened by clicking a row in the Admin → Users tab.
 *
 * Feb 2026 user spec: *"Make admin view of profiles clickable to show
 * activities. 1, 7, 30 days analytics."*
 */
import React, { useCallback, useEffect, useState } from 'react';
import { X, Activity, Coins, Award, BookOpen, Headphones, Upload, MessageSquare, Calendar, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WINDOWS = [
  { key: 1,  label: '1D' },
  { key: 7,  label: '7D' },
  { key: 30, label: '30D' },
];

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const StatCard = ({ icon, label, value, testId }) => (
  <div className="rounded-lg bg-slate-900/60 border border-slate-700 px-3 py-2.5" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 mb-1">
      {icon} {label}
    </div>
    <p className="text-white text-xl font-bold tabular-nums">{value}</p>
  </div>
);

const AdminUserActivityDialog = ({ userId, onClose }) => {
  const [windowDays, setWindowDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/users/${userId}/activity?window_days=${windowDays}`,
      );
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [userId, windowDays]);

  useEffect(() => { load(); }, [load]);

  if (!userId) return null;

  const u = data?.user;
  const totals = data?.totals || {};
  const breakdown = data?.breakdown || [];

  return (
    <div
      className="fixed inset-0 z-[240] flex items-center justify-center px-4 py-6 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="admin-user-activity-dialog"
    >
      <div className="w-full max-w-2xl bg-slate-900 border-2 border-indigo-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-indigo-900/40 to-slate-900">
          <div className="flex items-center gap-3 min-w-0">
            {u?.picture ? (
              <img src={u.picture} alt="" className="w-10 h-10 rounded-full shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
                {(u?.name || u?.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-white font-black text-base truncate">{u?.name || u?.email || 'User'}</h2>
              <p className="text-[11px] text-slate-400 truncate">
                {u?.email} · <span className="uppercase font-bold text-indigo-300">{u?.role || 'user'}</span>
                {u?.country && <> · {u.country}</>}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 -mr-1"
            data-testid="admin-user-activity-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Window pills */}
        <div className="px-5 pt-3 pb-2 border-b border-slate-800 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 mr-1">Window</span>
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowDays(w.key)}
              className={`text-xs px-3 py-1 rounded-full font-bold border transition-colors ${
                windowDays === w.key
                  ? 'bg-indigo-600 border-indigo-500 text-white'
                  : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
              }`}
              data-testid={`admin-user-activity-window-${w.key}`}
            >
              {w.label}
            </button>
          ))}
          <div className="ml-auto text-[11px] text-slate-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Since {fmt(data?.since)}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading activity…
            </div>
          )}
          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 text-sm text-rose-200">
              {error}
            </div>
          )}
          {!loading && !error && data && (
            <>
              {/* Top-line totals */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatCard icon={<Activity className="w-3 h-3" />} label="Events" value={totals.events || 0} testId="stat-events" />
                <StatCard icon={<Award className="w-3 h-3" />}    label="XP"     value={totals.xp     || 0} testId="stat-xp" />
                <StatCard icon={<Coins className="w-3 h-3" />}    label="Coins"  value={totals.coins  || 0} testId="stat-coins" />
                <StatCard icon={<Upload className="w-3 h-3" />}   label="Uploads" value={totals.uploads || 0} testId="stat-uploads" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatCard icon={<BookOpen className="w-3 h-3" />}   label="Books read"     value={totals.books_read      || 0} testId="stat-books" />
                <StatCard icon={<Headphones className="w-3 h-3" />} label="Audios listened" value={totals.audios_listened || 0} testId="stat-audios" />
                <StatCard icon={<MessageSquare className="w-3 h-3" />} label="Comments"    value={totals.comments        || 0} testId="stat-comments" />
                <StatCard icon={<BookOpen className="w-3 h-3" />}   label="Series created" value={(totals.text_series_created || 0) + (totals.audio_series_created || 0)} testId="stat-series" />
              </div>

              {/* Event-type breakdown */}
              <div>
                <h3 className="text-[10px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                  <Activity className="w-3 h-3" /> Event breakdown
                </h3>
                {breakdown.length === 0 ? (
                  <p className="text-slate-500 text-sm py-4 text-center" data-testid="admin-user-activity-empty">
                    No activity in this window.
                  </p>
                ) : (
                  <div className="rounded-lg border border-slate-700 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800/70 text-[10px] uppercase tracking-widest text-slate-400">
                        <tr>
                          <th className="px-3 py-2 text-left">Event</th>
                          <th className="px-3 py-2 text-right">Count</th>
                          <th className="px-3 py-2 text-right">XP</th>
                          <th className="px-3 py-2 text-right">Coins</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {breakdown.map((r) => (
                          <tr key={r.event_type} data-testid={`admin-user-activity-row-${r.event_type}`}>
                            <td className="px-3 py-2 text-slate-200">{r.event_type.replace(/_/g, ' ')}</td>
                            <td className="px-3 py-2 text-right text-white font-semibold tabular-nums">{r.count}</td>
                            <td className="px-3 py-2 text-right text-emerald-300 tabular-nums">{r.xp || 0}</td>
                            <td className="px-3 py-2 text-right text-amber-300 tabular-nums">{Number(r.coins || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer meta */}
              <div className="rounded-lg bg-slate-800/40 border border-slate-700 px-3 py-2 text-[11px] text-slate-400">
                Joined: <span className="text-slate-200">{fmt(u?.created_at)}</span>
                {' · '}Last seen: <span className="text-slate-200">{fmt(u?.last_seen_at)}</span>
                {u?.subscription_tier && <> · Tier: <span className="text-indigo-300 font-semibold">{u.subscription_tier}</span></>}
                {u?.vault_access_granted && <> · <span className="text-fuchsia-300 font-semibold">Vault access</span></>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserActivityDialog;
